import { useEffect, useState } from "react";
import { devError } from "@/lib/log";
import { api } from "@/lib/api";
import { fmtMoney, fmtNumber, monthLabel, FORMAT_LABELS, MARKET_LABELS, fmtDateRange, cycleShort } from "@/lib/fmt";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell,
} from "recharts";
import { Sparkles, RefreshCw, Calendar, Info } from "lucide-react";

const CHART_COLORS = ["#2708A1", "#D89B00", "#B519DE", "#0822D2", "#7A8F73"];

// Hoisted Recharts style objects — defining these in JSX would create new
// references each render and force unnecessary chart re-layouts.
const CHART_MARGIN = { top: 16, right: 16, left: 0, bottom: 8 };
const CHART_AXIS_TICK = { fontSize: 11, fill: "#5B5670" };
const CHART_TOOLTIP_STYLE = {
  background: "#fff",
  border: "1px solid #E5E2D8",
  borderRadius: 12,
  fontFamily: "Josefin Sans",
};
const PIE_TOOLTIP_STYLE = {
  background: "#fff",
  border: "1px solid #E5E2D8",
  borderRadius: 12,
};
const LINE_ACTIVE_DOT = { r: 6, fill: "#2708A1" };

// `dot` renderer hoisted out of <Line> so React doesn't see a new component
// type on every render (react/no-unstable-nested-components).
function TrendDot(props) {
  const { cx, cy, payload, index } = props;
  if (!payload?.has_data) {
    return <circle key={index} cx={cx} cy={cy} r={3} fill="#E5E2D8" stroke="#E5E2D8" />;
  }
  return <circle key={index} cx={cx} cy={cy} r={4} fill="#D89B00" stroke="#2708A1" strokeWidth={1.5} />;
}

function trendTooltipFormatter(v, n, p) {
  return [
    p?.payload?.has_data ? `${v} copies` : "Data not yet available",
    p?.payload?.has_data ? "Units" : "",
  ];
}

export default function AuthorDashboard() {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get("/me/dashboard")
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setErr(e.message || "Could not load dashboard"); });
    return () => { cancelled = true; };
  }, []);

  const onRefreshNextMove = async () => {
    setRefreshing(true);
    try {
      const { data: d } = await api.post("/me/next-move/refresh");
      setData((prev) => ({ ...prev, next_move: d.next_move }));
      toast.success("New insight ready");
    } catch (e) {
      devError("Refresh next move failed:", e);
      toast.error("Couldn't refresh insight — please try again in a moment");
    } finally {
      setRefreshing(false);
    }
  };

  if (err) return <div className="text-hh-danger" data-testid="dashboard-error">{err}</div>;
  if (!data) return <DashboardSkeleton />;

  const { author, summary, next_move: nextMove, titles, last_updated: lastUpdated } = data;
  const firstName = (author?.name || "there").split(" ")[0];
  const lastUpdatedLabel = (() => {
    if (!lastUpdated) return null;
    try {
      const d = new Date(lastUpdated);
      return d.toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });
    } catch (_e) { return null; }
  })();

  const fmtData = Object.entries(summary.format_split || {})
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: FORMAT_LABELS[k] || k, value: v }));
  // Use market_split (which separates Healing House direct from IngramSpark regional
  // channels) and fall back to region_split for backwards compatibility.
  const marketSource = summary.market_split || summary.region_split || {};
  const regData = Object.entries(marketSource)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: MARKET_LABELS[k] || k, value: v }));

  return (
    <div className="space-y-24" data-testid="author-dashboard">
      {/* Greeting */}
      <div className="animate-fade-up">
        <p className="hh-overline mb-3">For {firstName}</p>
        <h1 className="font-serif text-3xl sm:text-4xl text-hh-ink tracking-tight">
          Here is how your book is moving through the world.
        </h1>
        {lastUpdatedLabel && (
          <p className="mt-3 inline-flex items-center gap-2 text-xs text-hh-ink2 bg-hh-muted/60 border border-hh-border rounded-full px-3 py-1.5" data-testid="last-updated">
            <span className="h-1.5 w-1.5 rounded-full bg-hh-sage" />
            Sales data last refreshed <strong className="text-hh-ink font-medium">{lastUpdatedLabel}</strong>
          </p>
        )}
      </div>

      {/* 1. HERO NUMBER */}
      <section data-testid="hero-number-section" className="animate-fade-up">
        <p className="hh-overline mb-6">Total copies sold · since {summary.history_from}</p>
        <div className="flex items-baseline gap-8 flex-wrap">
          <div
            className="font-serif text-[88px] sm:text-[120px] md:text-[144px] text-hh-ink leading-none tracking-tight"
            data-testid="hero-number-lifetime"
          >
            {fmtNumber(summary.total_units_lifetime)}
          </div>
          <div className="text-hh-ink2 text-lg">
            <span data-testid="hero-number-period" className="font-medium text-hh-ink">
              {fmtNumber(summary.total_units_period)}
            </span>{" "}
            sold this cycle ({summary.cycle})
          </div>
        </div>
      </section>

      {/* 2. ROYALTY SNAPSHOT */}
      <section data-testid="royalty-snapshot-section" className="animate-fade-up">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
          <div>
            <p className="hh-overline mb-2">Royalty snapshot</p>
            <h2 className="font-serif text-2xl sm:text-3xl text-hh-ink">Every cent, laid out</h2>
          </div>
          <p className="text-xs text-hh-ink2 flex items-center gap-1.5">
            <Info size={12} strokeWidth={1.5} /> You earn {summary.royalty_pct}% of net publisher compensation
          </p>
        </div>
        <div className="text-sm text-hh-ink2 mb-6 bg-hh-muted/60 border border-hh-border rounded-lg px-4 py-3 max-w-3xl">
          <p>
            <strong className="text-hh-ink">⏱ Why your most recent month may not appear yet:</strong>{" "}
            IngramSpark reports sales <strong>~90 days after they happen</strong>. We update the portal as new
            remittances arrive from Ingram. The latest period we&apos;ve received and ingested is{" "}
            <span className="text-hh-ink font-medium">
              {summary.latest_period_on_file ? monthLabel(summary.latest_period_on_file) : "no data yet"}
            </span>.
          </p>
          <p className="mt-2 text-xs text-hh-ink2/80">
            HHP-website direct sales appear immediately — only IngramSpark sales have the 90-day delay.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <RoyaltyBreakdownCard
            testid="snapshot-cycle"
            label={`This cycle · ${cycleShort(summary.cycle)}`}
            footnote={fmtDateRange(summary.cycle_start, summary.cycle_end)}
            totals={summary.total_compensation_cycle}
            yourShare={summary.royalties_cycle}
            pubShare={summary.publisher_share_cycle}
            yourPct={summary.royalty_pct}
            pubPct={summary.publisher_pct}
          />

          <div
            className="rounded-xl p-7 bg-gradient-to-br from-[#1A1530] to-[#2708A1] text-white shadow-[0_12px_36px_rgba(39,8,161,0.18)]"
            data-testid="snapshot-next-payment"
          >
            <p className="hh-overline mb-4 text-white/70">Estimated next payment</p>
            <div className="flex items-center gap-3 mb-3">
              <Calendar size={20} strokeWidth={1.5} className="text-[#FFB800]" />
              <div className="font-serif text-3xl md:text-4xl tracking-tight">
                {summary.next_payment_date}
              </div>
            </div>
            <p className="text-sm text-white/70 leading-relaxed">
              Royalties pay quarterly — each quarter falls due at the end of the following quarter and is paid within 30 days (e.g. Apr–Jun is paid by 31 October).
            </p>
          </div>

          <RoyaltyBreakdownCard
            testid="snapshot-lifetime"
            label={`Totals since ${summary.history_from}`}
            footnote="Since publication"
            totals={summary.total_compensation_lifetime}
            yourShare={summary.royalties_lifetime}
            pubShare={summary.publisher_share_lifetime}
            yourPct={summary.royalty_pct}
            pubPct={summary.publisher_pct}
          />
        </div>
      </section>

      {/* 2b. CYCLE MONTHLY BREAKDOWN */}
      <section data-testid="cycle-breakdown-section" className="animate-fade-up">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <p className="hh-overline mb-2">{cycleShort(summary.cycle)} · month by month</p>
            <h2 className="font-serif text-2xl sm:text-3xl text-hh-ink">A six-month story</h2>
            <p className="text-xs text-hh-ink2 mt-2 max-w-xl">
              Numbers reflect year-to-date totals reported in each month&rsquo;s IngramSpark statement, so the most recent month carries the cumulative figure.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {summary.cycle_monthly_breakdown.map((m) => (
            <CycleMonth key={m.month} m={m} yourPct={summary.royalty_pct} />
          ))}
        </div>
      </section>

      {/* 3. SALES STORY CHART */}
      <section data-testid="sales-story-section" className="animate-fade-up">
        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="hh-overline mb-2">Your sales story</p>
            <h2 className="font-serif text-2xl sm:text-3xl text-hh-ink">The last 12 months</h2>
          </div>
        </div>
        <div className="hh-card p-6 md:p-8">
          <p className="text-xs text-hh-ink2 mb-4 bg-hh-muted/50 border border-hh-border rounded-md px-3 py-2 inline-block">
            Each point is the year-to-date total reported in that month&rsquo;s IngramSpark statement.
          </p>
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={summary.monthly_trend} margin={CHART_MARGIN}>
                <CartesianGrid stroke="#E5E2D8" strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="month" tickFormatter={monthLabel} tick={CHART_AXIS_TICK} stroke="#E5E2D8" />
                <YAxis tick={CHART_AXIS_TICK} stroke="#E5E2D8" allowDecimals={false} />
                <Tooltip
                  contentStyle={CHART_TOOLTIP_STYLE}
                  labelFormatter={monthLabel}
                  formatter={trendTooltipFormatter}
                />
                <Line
                  type="monotone"
                  dataKey="units"
                  stroke="#2708A1"
                  strokeWidth={2.5}
                  dot={TrendDot}
                  activeDot={LINE_ACTIVE_DOT}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* 4. SALES BREAKDOWN */}
      <section data-testid="sales-breakdown-section" className="animate-fade-up">
        <p className="hh-overline mb-6">Sales breakdown</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <BreakdownCard title="By format" data={fmtData} testid="breakdown-format" />
          <BreakdownCard title="By sales market" data={regData} testid="breakdown-market" />
        </div>
      </section>

      {/* 4b. YOUR CATALOGUE — all titles, including 0-sale */}
      {titles && titles.length > 0 && (
        <section data-testid="catalogue-section" className="animate-fade-up">
          <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
            <div>
              <p className="hh-overline mb-2">Your catalogue</p>
              <h2 className="font-serif text-2xl sm:text-3xl text-hh-ink">Every edition we publish for you</h2>
            </div>
            <p className="text-xs text-hh-ink2">{titles.length} edition{titles.length === 1 ? "" : "s"}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {titles.map((t) => (
              <CatalogueRow key={t.id} t={t} yourPct={summary.royalty_pct} historyFrom={summary.history_from} />
            ))}
          </div>
        </section>
      )}

      {/* 5. YOUR NEXT MOVE */}
      <section data-testid="next-move-section" className="animate-fade-up">
        <p className="hh-overline mb-6">From your publisher · A note for {firstName}</p>
        <div className="rounded-2xl bg-hh-muted border border-hh-border p-8 md:p-12 relative" data-testid="next-move-card">
          <div className="flex items-start gap-5">
            <span className="hidden sm:grid place-items-center h-12 w-12 rounded-full bg-white border border-hh-border shrink-0">
              <Sparkles size={18} strokeWidth={1.5} className="text-hh-primary" />
            </span>
            <div className="flex-1">
              <h3 className="font-serif text-2xl sm:text-3xl text-hh-ink mb-4 tracking-tight">Your next move</h3>
              <p className="font-serif text-lg sm:text-xl text-hh-ink/90 leading-relaxed italic" data-testid="next-move-text">
                {nextMove || "We are preparing a personal note for you. Check back in a moment."}
              </p>
              <button
                onClick={onRefreshNextMove}
                disabled={refreshing}
                className="mt-6 inline-flex items-center gap-2 text-xs text-hh-ink2 hover:text-hh-ink transition-colors disabled:opacity-50"
                data-testid="next-move-refresh"
              >
                <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} strokeWidth={1.5} />
                {refreshing ? "Rewriting..." : "Ask for a fresh take"}
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function CatalogueRow({ t, yourPct, historyFrom }) {
  const royaltyEntries = Object.entries(t.lifetime_royalties || {}).filter(([, v]) => Math.abs(v) > 0.001);
  const hasSales = (t.lifetime_units || 0) !== 0;
  const fmtBadge = t.format === "ebook" ? "eBook" : t.format === "hardcover" ? "Hardcover" : "Paperback";
  return (
    <div
      className="hh-card hh-card-hover p-5 flex gap-4 items-start"
      data-testid={`catalogue-${t.isbn}`}
    >
      <div className="h-20 w-14 rounded-md bg-hh-muted border border-hh-border overflow-hidden shrink-0 grid place-items-center">
        {t.cover_url ? (
          <img src={t.cover_url} alt="" className="h-full w-full object-cover" />
        ) : (
          <span className="text-[9px] text-hh-ink2 tracking-widest uppercase">Cover<br/>soon</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2 flex-wrap">
          <h3 className="font-serif text-base text-hh-ink leading-snug truncate" title={t.title}>
            {t.title}
          </h3>
          <span className="text-[10px] uppercase tracking-widest text-hh-ink2 shrink-0">{fmtBadge}</span>
        </div>
        <p className="text-[11px] text-hh-ink2 font-mono mt-0.5">ISBN {t.isbn}</p>
        <div className="mt-3 pt-3 border-t border-hh-border/70">
          {hasSales ? (
            <div className="space-y-1">
              <div className="flex justify-between text-xs">
                <span className="text-hh-ink2">Copies sold since {historyFrom}</span>
                <span className="font-medium text-hh-ink" data-testid={`catalogue-${t.isbn}-units`}>{t.lifetime_units}</span>
              </div>
              {royaltyEntries.map(([ccy, v]) => (
                <div key={ccy} className="flex justify-between text-xs">
                  <span className="text-hh-ink2">Your {yourPct}% · {ccy}</span>
                  <span className="text-hh-primary font-medium">{fmtMoney(v, ccy)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[11px] text-hh-ink2 italic" data-testid={`catalogue-${t.isbn}-nosales`}>
              No sales yet — we&rsquo;re tracking from {t.publication_date ? "publication" : "day one"}.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function CycleMonth({ m, yourPct }) {
  const royaltyEntries = Object.entries(m.royalties || {}).filter(([, v]) => Math.abs(v) > 0.001);
  const [y, mm] = m.month.split("-").map(Number);
  const labelMonth = new Date(y, mm - 1, 1).toLocaleString(undefined, { month: "short" });

  if (!m.has_data) {
    return (
      <div className="rounded-xl border border-dashed border-hh-border bg-hh-muted/30 p-4 min-h-[120px]" data-testid={`cycle-month-${m.month}`}>
        <div className="hh-overline mb-1 text-hh-ink2/70">{labelMonth}</div>
        <div className="text-xs text-hh-ink2 italic mt-3">Data not yet available</div>
      </div>
    );
  }
  if (m.units === 0 && royaltyEntries.length === 0) {
    return (
      <div className="rounded-xl border border-hh-border bg-white p-4 min-h-[120px]" data-testid={`cycle-month-${m.month}`}>
        <div className="hh-overline mb-1">{labelMonth}</div>
        <div className="font-serif text-2xl text-hh-ink mt-1">0</div>
        <div className="text-[10px] text-hh-ink2 mt-2 uppercase tracking-widest">No sales</div>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-hh-border bg-white p-4 min-h-[120px]" data-testid={`cycle-month-${m.month}`}>
      <div className="hh-overline mb-1">{labelMonth}</div>
      <div className="font-serif text-2xl text-hh-ink mt-1" data-testid={`cycle-month-${m.month}-units`}>{m.units}</div>
      <div className="text-[10px] text-hh-ink2 mt-1 uppercase tracking-widest">{m.units === 1 ? "copy" : "copies"}</div>
      {royaltyEntries.length > 0 && (
        <div className="mt-3 pt-3 border-t border-hh-border/70 space-y-0.5">
          {royaltyEntries.map(([ccy, v]) => (
            <div key={ccy} className="text-xs flex justify-between" data-testid={`cycle-month-${m.month}-${ccy}`}>
              <span className="text-hh-ink2">{ccy}</span>
              <span className="text-hh-primary font-medium">{fmtMoney(v, ccy)}</span>
            </div>
          ))}
          <div className="text-[9px] text-hh-ink2/70 mt-1">Your {yourPct}%</div>
        </div>
      )}
    </div>
  );
}

function RoyaltyBreakdownCard({ label, footnote, totals, yourShare, pubShare, yourPct, pubPct, testid }) {
  const currencies = Array.from(new Set([
    ...Object.keys(totals || {}),
    ...Object.keys(yourShare || {}),
  ]));
  const hasAny = currencies.length > 0 && currencies.some((c) => (totals?.[c] || 0) > 0);

  return (
    <div className="hh-card hh-card-hover p-7 flex flex-col" data-testid={testid}>
      <p className="hh-overline mb-5">{label}</p>
      {!hasAny ? (
        <>
          <div className="font-serif text-3xl text-hh-ink leading-tight">—</div>
          <p className="mt-3 text-xs text-hh-ink2">No sales recorded yet</p>
        </>
      ) : (
        <div className="space-y-4">
          {currencies.map((ccy) => (
            <div key={ccy} className="border border-hh-border rounded-lg overflow-hidden">
              <div className="px-4 py-2.5 bg-hh-muted/60 flex items-center justify-between">
                <span className="text-xs font-semibold text-hh-ink tracking-widest">{ccy}</span>
                <span className="text-xs text-hh-ink2">Total compensation paid</span>
                <span className="font-serif text-base text-hh-ink" data-testid={`${testid}-${ccy}-total`}>
                  {fmtMoney(totals[ccy] || 0, ccy)}
                </span>
              </div>
              <div className="grid grid-cols-2 divide-x divide-hh-border">
                <div className="px-4 py-3">
                  <div className="text-[10px] uppercase tracking-widest text-hh-ink2 mb-1">You · {yourPct}%</div>
                  <div className="font-serif text-xl text-hh-primary" data-testid={`${testid}-${ccy}-author`}>
                    {fmtMoney(yourShare[ccy] || 0, ccy)}
                  </div>
                </div>
                <div className="px-4 py-3 bg-hh-muted/30">
                  <div className="text-[10px] uppercase tracking-widest text-hh-ink2 mb-1">Healing House · {pubPct}%</div>
                  <div className="font-serif text-xl text-hh-ink2" data-testid={`${testid}-${ccy}-publisher`}>
                    {fmtMoney(pubShare[ccy] || 0, ccy)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {footnote && <p className="mt-4 text-xs text-hh-ink2">{footnote}</p>}
    </div>
  );
}

function BreakdownCard({ title, data, testid }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <div className="hh-card p-6 md:p-8" data-testid={testid}>
      <h3 className="font-serif text-xl text-hh-ink mb-2">{title}</h3>
      <p className="text-xs text-hh-ink2 mb-4">{total} copies total</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
        <div style={{ width: "100%", height: 200 }}>
          {data.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-hh-ink2">No data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} innerRadius={48} outerRadius={80} paddingAngle={2} dataKey="value" stroke="#FBFAF6" strokeWidth={2}>
                  {data.map((d, i) => <Cell key={d.name} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip
                  contentStyle={PIE_TOOLTIP_STYLE}
                  formatter={(v, n) => [`${v} copies`, n]}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
        <ul className="space-y-2">
          {data.map((d, i) => (
            <li key={d.name} className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 text-hh-ink">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                {d.name}
              </span>
              <span className="text-hh-ink2 text-xs">{d.value} · {total ? Math.round((d.value / total) * 100) : 0}%</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-12 animate-pulse">
      <div className="h-12 w-2/3 bg-hh-muted rounded-lg" />
      <div className="h-32 w-1/2 bg-hh-muted rounded-lg" />
      <div className="grid grid-cols-3 gap-6">
        <div className="h-40 bg-hh-muted rounded-xl" />
        <div className="h-40 bg-hh-muted rounded-xl" />
        <div className="h-40 bg-hh-muted rounded-xl" />
      </div>
      <div className="h-80 bg-hh-muted rounded-xl" />
    </div>
  );
}
